<template>
	<!-- LSP-21125: the caption lines saved during a live call, shown in the lightbox in place of Comments (the
	     right column on desktop, below the playing video at phone width). The parent only mounts this when the
	     recording HAS lines, so there is no empty state here. -->
	<div v-test="'recording-translation-panel'" class="d-flex flex-column ga-3 h-100 recording-translation-panel">
		<div class="d-flex align-center justify-space-between ga-2">
			<v-card-title class="pa-0 d-flex align-baseline">
				<div class="d-flex align-center translation-header">
					<v-icon>mdi-translate</v-icon>
					<span v-test="'recording-translation-title'" class="heading">Translation ({{ captions.utterances.length }})</span>
				</div>
			</v-card-title>
			<!-- LSP-21125 QA rework (Oliver): the way back to Comments, top-right of the panel (at phone width it only
			     closes the panel). -->
			<v-btn
				v-test="'recording-translation-close'"
				v-track="'photos-lightbox-translation-close-button'"
				icon="mdi-close"
				variant="text"
				size="small"
				:aria-label="closeLabel"
				@click="emit('close')" />
		</div>
		<!-- No language picker: every saved translation is into the language the host read the call in. -->
		<div v-if="hostLanguageName || captions.timesApproximate" class="d-flex flex-column ga-1">
			<div v-if="hostLanguageName" v-test="'recording-translation-host-language'" class="text-caption text-medium-emphasis">
				Translated into {{ hostLanguageName }}
			</div>
			<div v-if="captions.timesApproximate" v-test="'recording-translation-approximate'" class="text-caption text-medium-emphasis">
				Times are approximate
			</div>
		</div>
		<div ref="rowsContainer" v-test="'recording-translation-rows'" class="flex-grow-1 overflow-y-auto translation-rows">
			<div
				v-for="(row, index) in rows"
				:key="`${row.start}-${index}`"
				:ref="(element) => setRowElement(element, index)"
				v-test="'recording-translation-row'"
				:data-active="index === activeIndex ? 'true' : 'false'"
				:class="{ 'translation-row--active': index === activeIndex }"
				class="d-flex ga-2 px-2 py-2 translation-row"
				role="button"
				tabindex="0"
				:aria-current="index === activeIndex ? 'true' : undefined"
				:aria-label="`Play from ${row.timecode}`"
				@click="emit('seek', row.start)"
				@keydown.enter.prevent="emit('seek', row.start)"
				@keydown.space.prevent="emit('seek', row.start)">
				<v-avatar size="24" color="primary" class="flex-shrink-0">
					<span class="text-caption font-weight-bold">{{ row.initials }}</span>
				</v-avatar>
				<div class="d-flex flex-column flex-grow-1 translation-row-body">
					<div class="d-flex align-center ga-2 text-caption text-medium-emphasis">
						<span v-test="'recording-translation-row-speaker'" class="font-weight-bold text-high-emphasis text-truncate">{{
							row.speakerName
						}}</span>
						<span v-test="'recording-translation-row-time'" class="translation-row-time">{{ row.timecode }}</span>
					</div>
					<div v-test="'recording-translation-row-text'" class="text-body-2">{{ row.display.text }}</div>
					<div
						v-if="row.display.original"
						v-test="'recording-translation-row-original'"
						class="text-caption text-medium-emphasis font-italic">
						{{ row.display.original }}
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { computed, nextTick, ref, watch, type ComponentPublicInstance } from "vue";
	import type { RecordingCaptionsResponse } from "@/types/RecordingCaptions";
	import { captionLanguageName, findActiveUtteranceIndex, resolveUtteranceDisplay, speakerInitials } from "@/helpers/recordingCaptions";
	import { formatSeconds } from "@/helpers/time";

	const props = withDefaults(
		defineProps<{
			captions: RecordingCaptionsResponse;
			/** The lightbox video's playhead, in seconds from the start of the file. */
			currentTime: number;
			/** The X's accessible name: desktop goes back to Comments, phone width only closes the panel. */
			closeLabel?: string;
		}>(),
		{ closeLabel: "Back to comments" }
	);

	const emit = defineEmits<{
		seek: [seconds: number];
		/** The X: the lightbox closes the panel and shows Comments again. */
		close: [];
	}>();

	const rowsContainer = ref<HTMLElement | null>(null);
	const rowElements: HTMLElement[] = [];
	const setRowElement = (element: Element | ComponentPublicInstance | null, index: number) => {
		if (element instanceof HTMLElement) rowElements[index] = element;
	};

	const scrollActiveRowIntoView = async () => {
		await nextTick();
		const element = rowElements[activeIndex.value];
		if (typeof element?.scrollIntoView === "function") {
			element.scrollIntoView({ block: "nearest", behavior: "smooth" });
		}
	};

	// A different recording's captions arrive when the lightbox reuses this panel across navigation (the
	// Translation toggle stays on): start its transcript fresh. The rows container is the same element, so it
	// would otherwise keep the previous transcript's scroll offset — and the active-row watcher below does not
	// fire when both recordings' active index happens to match. This pre-flush watcher runs before the new rows
	// render, so clearing `rowElements` here drops the previous recording's nodes before the new ones register.
	watch(
		() => props.captions,
		() => {
			rowElements.length = 0;
			if (rowsContainer.value) rowsContainer.value.scrollTop = 0;
			void scrollActiveRowIntoView();
		}
	);

	// `hostLanguage` is null when nothing was translated (a same-language call); older API builds omit it.
	const hostLanguage = computed(() => props.captions.hostLanguage ?? null);
	const hostLanguageName = computed(() => (hostLanguage.value ? captionLanguageName(hostLanguage.value) : null));

	const rows = computed(() =>
		props.captions.utterances.map((utterance) => ({
			start: utterance.start,
			speakerName: utterance.speakerName,
			initials: speakerInitials(utterance.speakerName),
			timecode: formatSeconds(utterance.start),
			display: resolveUtteranceDisplay(utterance, hostLanguage.value)
		}))
	);

	const activeIndex = computed(() => findActiveUtteranceIndex(props.captions.utterances, props.currentTime));

	// Scroll only when the ACTIVE ROW changes, not on every time update, so a viewer reading further down the
	// list is not yanked back several times a second while one utterance plays. `immediate`: the panel is
	// usually opened mid-playback, and the row already playing must be brought into view on open too — the
	// nextTick waits for the rows to mount before reading their elements.
	watch(
		activeIndex,
		(index) => {
			if (index >= 0) void scrollActiveRowIntoView();
		},
		{ immediate: true }
	);

	defineExpose({ activeIndex });
</script>

<style scoped>
	.recording-translation-panel {
		min-height: 0;
	}

	.translation-header {
		color: #03011d;
	}

	.translation-header .v-icon {
		width: 28px;
		height: 28px;
		color: rgb(var(--v-theme-primary));
		margin-right: 8px;
		padding: 4px;
	}

	.translation-rows {
		min-height: 0;
	}

	.translation-row {
		border-left: 3px solid transparent;
		border-radius: 4px;
		cursor: pointer;
		transition: background-color 0.12s;
	}

	.translation-row:hover,
	.translation-row:focus-visible {
		background-color: rgba(var(--v-theme-primary), 0.06);
		outline: none;
	}

	.translation-row--active {
		background-color: rgba(var(--v-theme-accent), 0.45);
		border-left-color: rgb(var(--v-theme-primary));
	}

	.translation-row-body {
		min-width: 0;
	}

	.translation-row-time {
		font-variant-numeric: tabular-nums;
	}
</style>
